import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const loginData = { password };
      if (requires2FA && twoFACode) {
        loginData.totp_code = twoFACode;
      }

      const response = await axios.post(`${API}/admin/login`, loginData);
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setError('Please enter your 6-digit authentication code from Google Authenticator');
        setLoading(false);
        return;
      }

      if (response.data.success) {
        // Store JWT token
        localStorage.setItem('adminToken', response.data.token);
        localStorage.setItem('adminAuth', 'true');
        
        // Check if password reset required
        if (response.data.password_reset_required) {
          navigate('/admin/password-reset');
        } else {
          navigate('/admin/dashboard');
        }
      }
    } catch (error) {
      if (requires2FA) {
        setError('Invalid 2FA code. Please try again.');
      } else {
        setError('Invalid password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E3F2FD] to-white flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_giftcard-vault-1/artifacts/03yh11a7_MethodMate%20Main%20Logo%20%281%29.png" 
              alt="MethodMate" 
              className="h-16 mx-auto mb-4"
            />
            <h1 className="text-3xl font-bold text-black mb-2">Admin Login</h1>
            <p className="text-gray-600">Enter your password to access the dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-black mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                  placeholder="Enter admin password"
                  required
                  data-testid="admin-password-input"
                  disabled={requires2FA}
                />
                <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-3.5" />
              </div>
            </div>

            {requires2FA && (
              <div>
                <label className="block text-sm font-semibold text-black mb-2">
                  🔐 Two-Factor Authentication Code
                </label>
                <input
                  type="text"
                  maxLength="6"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent text-center text-2xl font-mono tracking-widest"
                  placeholder="000000"
                  required
                  autoFocus
                />
                <p className="text-xs text-gray-600 mt-2">
                  Enter the 6-digit code from your Google Authenticator app
                </p>
              </div>
            )}

            {error && (
              <div className={`px-4 py-3 rounded-lg text-sm ${
                requires2FA && !error.includes('Invalid')
                  ? 'bg-blue-50 border border-blue-200 text-blue-700'
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-[#1E88E5] text-white font-semibold rounded-lg hover:bg-[#1565C0] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Logging in...</span>
                </>
              ) : (
                <span>Login to Admin Panel</span>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-[#1E88E5] hover:text-[#1565C0] text-sm font-medium"
            >
              ← Back to Home
            </button>
          </div>
        </div>

        <p className="text-center text-gray-600 text-sm mt-4">
          Secure admin access
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;